import React from 'react';
import { PureQueryOptions } from '@apollo/client';
import { useState } from 'react';
import {
  buildWorkloadPrefill,
  createNewTimeEntry,
  DayWithTimeEntries,
  TimeEntryItem,
  TimeEntryReadOnlyReason,
} from '../logic';
import DayTimeEntriesDumb from './DayTimeEntriesDumb';
import { CostCenterProps } from '..';
import { WarningModal } from '../../components/WarningModal/WarningModal';
import {
  useTimeTrackingRestriction,
  useTimeTrackingSettingsAllDays,
} from '../../../../hooks/timeTrackingSettings';
import { toISODateOnly } from '../../../../../../utils/dateUtils';
import { ProjectWithStatus } from '../../../Projects/types';
import { getProjectStatus } from '../../../Projects/ProjectsTimesheets/helpers';
import { useMemo } from 'react';
import { WorkloadTrackingType } from '../../../../__generated__/graphql';

type Props = {
  day: DayWithTimeEntries;
  costCenters: readonly CostCenterProps[];
  refetchQueriesForEmployeeMode?: PureQueryOptions[];
  readonlyReasons: TimeEntryReadOnlyReason[];
  projects?: ProjectWithStatus[];
};

const DayTimeEntriesSmart: React.FC<React.PropsWithChildren<Props>> = ({
  day,
  costCenters,
  readonlyReasons,
  refetchQueriesForEmployeeMode,
  projects,
}) => {
  const dayProjects: ProjectWithStatus[] = useMemo(() => {
    if (!projects) return [];
    return projects.map((project) => ({
      ...project,
      status: getProjectStatus(project, day.day),
    }));
  }, [projects, day.day]);
  const [timeEntryNew, setTimeEntryNew] = useState<TimeEntryItem | null>(null);
  const {
    showRestrictedWarningModal,
    setShowRestrictedWarningModal,
    getTimeTrackingRestrictedReason,
  } = useTimeTrackingRestriction();
  const { getTimeTrackingSettingForDay } = useTimeTrackingSettingsAllDays();
  const timeTrackingSettings = getTimeTrackingSettingForDay(day.day.toDate());
  const isTimeTrackingWithProjects =
    timeTrackingSettings?.workload?.flair__Time_Tracking_Type__c ===
    WorkloadTrackingType.TimeTrackingWithProjects;

  const restrictionReason = getTimeTrackingRestrictedReason(
    toISODateOnly(day.day.toDate()),
  );

  const handleAddNewTimeEntryClick = () => {
    if (restrictionReason) {
      setShowRestrictedWarningModal(true);
    } else {
      const startTime =
        day.timeEntries.length > 0
          ? day.timeEntries[day.timeEntries.length - 1].end?.time
          : day.workload?.startTime;
      setTimeEntryNew(
        createNewTimeEntry(
          day.day,
          day.timeSheetId,
          buildWorkloadPrefill(day.workload, startTime),
        ),
      );
    }
  };

  const handleCancelCreatingNewTimeEntry = () => {
    setTimeEntryNew(null);
  };

  const timeEntries = timeEntryNew
    ? [...day.timeEntries, timeEntryNew]
    : day.timeEntries;

  return (
    <>
      <WarningModal
        visible={showRestrictedWarningModal}
        restrictionReason={restrictionReason}
        onClose={() => setShowRestrictedWarningModal(false)}
      />
      <DayTimeEntriesDumb
        day={day}
        costCenters={costCenters}
        employeeType={day.employeeType}
        readonlyReasons={readonlyReasons}
        timeEntries={timeEntries}
        refetchQueriesForEmployeeMode={refetchQueriesForEmployeeMode}
        onAddNewTimeEntryClick={handleAddNewTimeEntryClick}
        onCancelCreatingNewTimeEntry={handleCancelCreatingNewTimeEntry}
        projects={dayProjects}
        isTimeTrackingWithProjects={isTimeTrackingWithProjects}
      />
    </>
  );
};

export default DayTimeEntriesSmart;
